import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Button, Container } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';

// Components
import Layout from './components/Layout';
import Home from './components/Home';
import Console from './components/Console';
import Profile from './components/Profile';
import Login from './components/Login';
import AuthCallback from './auth/AuthCallback';
import ProtectedRoute from './auth/ProtectedRoute';
import { MetricsPage } from './components/metrics';
import CollectionPage from './components/Collection';
import Harmony from './pages/Harmony/Harmony';
import Symphony from './pages/Symphony/Symphony';

// Providers
import AuthProvider, { useAuth } from './auth/AuthProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './components/ui/Toast';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// API Client Setup
import apiClient from './api/client';
import logger from './utils/logger';

// Material UI Theme Configuration
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

// Error Fallback Component
interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          p: 3,
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600 }}>
          An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
        </Typography>
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1, maxWidth: 800, overflow: 'auto' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'left' }}>
            {error.message}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={resetErrorBoundary}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/'}
          >
            Go Home
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

// 404 Not Found Component
const NotFound: React.FC = () => (
  <Container maxWidth="md">
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Typography variant="h1" color="primary" sx={{ fontSize: '8rem', fontWeight: 'bold' }}>
        404
      </Typography>
      <Typography variant="h4" gutterBottom>
        Page Not Found
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The page you're looking for doesn't exist or has been moved.
      </Typography>
      <Button
        variant="contained"
        onClick={() => window.location.href = '/'}
      >
        Go Home
      </Button>
    </Box>
  </Container>
);

// Access Denied Component
const AccessDenied: React.FC = () => (
  <Container maxWidth="md">
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Typography variant="h4" color="error" gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        You don't have permission to access this resource.
      </Typography>
      <Button
        variant="contained"
        onClick={() => window.location.href = '/'}
      >
        Go Home
      </Button>
    </Box>
  </Container>
);

// Placeholder components for other routes
const Dashboard: React.FC = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Dashboard</Typography>
    <Typography variant="body1">Dashboard content coming soon...</Typography>
  </Box>
);

const Settings: React.FC = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Settings</Typography>
    <Typography variant="body1">Settings content coming soon...</Typography>
  </Box>
);

const Admin: React.FC = () => (
  <Box>
    <Typography variant="h4" gutterBottom>Admin Panel</Typography>
    <Typography variant="body1">Admin content coming soon...</Typography>
  </Box>
);

// Redirect /radar/* → /rubric/* preserving sub-path
const RadarRedirect: React.FC = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/radar/, '/rubric');
  return <Navigate to={newPath + location.search + location.hash} replace />;
};

// App Setup Component (handles UserManager setup)
const AppSetup: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userManager } = useAuth();

  useEffect(() => {
    // Configure the API client with the UserManager
    if (userManager) {
      apiClient.setUserManager(userManager);
      logger.info('API client configured with UserManager');
    }
  }, [userManager]);

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  useEffect(() => {
    logger.info('Application started', {
      version: import.meta.env.VITE_VERSION || '1.0.0',
      environment: import.meta.env.MODE,
      timestamp: new Date().toISOString(),
    });
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        logger.error('Application error boundary triggered', {
          error: error.message,
          stack: error.stack,
          errorInfo: errorInfo.componentStack,
        });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <AppSetup>
              <ToastProvider>
              <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/callback" element={<AuthCallback />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/access-denied" element={<AccessDenied />} />

                {/* Protected Routes - Console as main route */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Console />
                  </ProtectedRoute>
                } />
                <Route path="/rubric/*" element={
                  <ProtectedRoute>
                    <Console />
                  </ProtectedRoute>
                } />
                <Route path="/radar/*" element={<RadarRedirect />} />
                
                {/* Profile route accessible from Console */}
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                
                {/* Metrics Management route */}
                <Route path="/metrics" element={
                  <ProtectedRoute>
                    <MetricsPage />
                  </ProtectedRoute>
                } />

                {/* Collection Management route */}
                <Route path="/collection" element={
                  <ProtectedRoute>
                    <CollectionPage />
                  </ProtectedRoute>
                } />

                {/* Harmony Operational Knowledge Management route */}
                <Route path="/harmony" element={
                  <ProtectedRoute>
                    <Harmony />
                  </ProtectedRoute>
                } />

                {/* Symphony Knowledge Management route */}
                <Route path="/symphony" element={
                  <ProtectedRoute>
                    <Symphony />
                  </ProtectedRoute>
                } />

                {/* Legacy routes with Layout wrapper */}
                <Route path="/legacy" element={<Layout />}>
                  <Route index element={
                    <ProtectedRoute>
                      <Home />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="profile" element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="admin" element={
                    <ProtectedRoute requiredRoles={['admin', 'manager']}>
                      <Admin />
                    </ProtectedRoute>
                  } />
                </Route>

                {/* Catch-all route for 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Router>
              </ToastProvider>
            </AppSetup>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;